import React, { useState, useEffect } from 'react';
import { generateContent, getAvailableModels } from '../services/openRouterService';
import { cleanAIReport } from '../utils/reportCleaner';
import { markdownToHTML } from '../utils/markdownConverter';
import { isWeb } from '../utils/platform';
import './FBSearchTrend.css';

const FBSearchTrend = () => {
  const [keyword, setKeyword] = useState('');
  const [status, setStatus] = useState('idle'); // idle, scraping, analyzing, success, error
  const [report, setReport] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [rateLimitInfo, setRateLimitInfo] = useState({ remaining: 5, resetTime: null });
  const [isLoggedIn, setIsLoggedIn] = useState(null); // null = unknown, true/false = checked
  const [useAdvancedMode, setUseAdvancedMode] = useState(false); // Toggle for Advanced Search mode
  const [showBrowserWindow, setShowBrowserWindow] = useState(true); // Show browser window during extraction

  // Rate limiting: 5 searches per hour
  const RATE_LIMIT = 5;
  const RATE_LIMIT_WINDOW = 60 * 60 * 1000; // 1 hour in milliseconds

  // Check rate limit
  const checkRateLimit = () => {
    const searches = JSON.parse(localStorage.getItem('fb_search_trend_searches') || '[]');
    const now = Date.now();
    const recentSearches = searches.filter(timestamp => now - timestamp < RATE_LIMIT_WINDOW);
    
    const remaining = Math.max(0, RATE_LIMIT - recentSearches.length);
    const oldestSearch = recentSearches.length > 0 ? Math.min(...recentSearches) : null;
    const resetTime = oldestSearch ? new Date(oldestSearch + RATE_LIMIT_WINDOW) : null;

    setRateLimitInfo({ remaining, resetTime });
    return remaining > 0;
  };

  // Record search for rate limiting
  const recordSearch = () => {
    const searches = JSON.parse(localStorage.getItem('fb_search_trend_searches') || '[]');
    const now = Date.now();
    const recentSearches = searches.filter(timestamp => now - timestamp < RATE_LIMIT_WINDOW);
    recentSearches.push(now);
    localStorage.setItem('fb_search_trend_searches', JSON.stringify(recentSearches));
    checkRateLimit();
  };

  // Check login status on mount
  useEffect(() => {
    // Wait for electronAPI to be available
    let retryCount = 0;
    const maxRetries = 50; // 10 seconds total
    
    const checkAPI = () => {
      // Check if electronAPI exists and has fbSearchTrend
      if (window.electronAPI) {
        console.log('electronAPI found:', Object.keys(window.electronAPI));
        
        if (window.electronAPI.fbSearchTrend) {
          console.log('fbSearchTrend API found');
          window.electronAPI.fbSearchTrend.checkLogin().then((result) => {
            if (result.success) {
              setIsLoggedIn(result.isLoggedIn);
            }
          }).catch((error) => {
            console.error('Error checking login status:', error);
          });
          return; // Success, stop retrying
        } else {
          console.warn('electronAPI exists but fbSearchTrend is missing. Available keys:', Object.keys(window.electronAPI));
        }
      } else {
        console.warn('electronAPI not found, retry', retryCount);
      }
      
      // Retry if not found and haven't exceeded max retries
      if (retryCount < maxRetries) {
        retryCount++;
        setTimeout(checkAPI, 200);
      } else {
        console.error('Failed to find electronAPI.fbSearchTrend after', maxRetries, 'retries');
      }
    };
    
    // Start checking after a short delay to allow preload script to run
    setTimeout(checkAPI, 100);
    checkRateLimit();
  }, []);

  if (isWeb) {
    return (
      <div className="fb-search-trend">
        <div className="fb-search-trend-header">
          <h2>📊 FB Search Trend</h2>
          <p style={{ padding: 24, textAlign: 'center', fontSize: 16 }}>
            FB Search Trend is available in the Fiddyscript desktop app.
          </p>
        </div>
      </div>
    );
  }

  // The orchestrator function
  const handleSearch = async () => {
    if (!keyword.trim()) {
      setErrorMessage('Please enter a keyword to search');
      setStatus('error');
      return;
    }

    // Check rate limit
    if (!checkRateLimit()) {
      setErrorMessage(`Rate limit exceeded. Maximum ${RATE_LIMIT} searches per hour. Please try again later.`);
      setStatus('error');
      return;
    }

    setStatus('scraping');
    setReport(null);
    setErrorMessage('');

    try {
      // Step A: Call Electron to scrape Facebook
      // Wait a bit for electronAPI to be available if it's not ready yet
      let retries = 0;
      while ((!window.electronAPI || !window.electronAPI.fbSearchTrend) && retries < 10) {
        await new Promise(resolve => setTimeout(resolve, 100));
        retries++;
      }

      if (!window.electronAPI) {
        console.error('window.electronAPI is not defined');
        console.error('Available on window:', Object.keys(window).filter(k => k.includes('electron') || k.includes('api')));
        throw new Error('FB Search Trend feature requires the desktop app. Please ensure you are running the Electron version.');
      }
      
      if (!window.electronAPI.fbSearchTrend) {
        console.error('window.electronAPI.fbSearchTrend is not defined');
        console.error('Available electronAPI methods:', Object.keys(window.electronAPI));
        throw new Error('FB Search Trend API is not available. The preload script may not have loaded correctly. Please restart the app.');
      }

      // Call Electron to scrape Facebook (with advanced mode flag and showWindow flag)
      const result = await window.electronAPI.fbSearchTrend.search(keyword.trim(), useAdvancedMode, showBrowserWindow);

      if (!result.success) {
        if (result.error && result.error.includes('log in')) {
          setIsLoggedIn(false);
          throw new Error('Please log in to Facebook. Click "Login to Facebook" button to open the login window.');
        }
        throw new Error(result.error || 'Failed to search Facebook');
      }

      let rawData = result.data;
      
      // If advanced mode and screenshot is available, use vision API
      if (useAdvancedMode && result.data.screenshot) {
        try {
          const apiKey = localStorage.getItem('openrouter_api_key');
          if (!apiKey || apiKey.trim() === '') {
            throw new Error('OpenRouter API key is required for Advanced mode');
          }
          
          const { generateVisionContent } = await import('../services/openRouterService');
          const visionModel = 'openai/gpt-4o';
          const visionPrompt = `Extract all autocomplete keyword suggestions visible in this Facebook search dropdown. Return ONLY a JSON array of keywords, one per line. Filter out URLs, domains (anything with .com, .net, etc.), spam, and random concatenated text. Only include actual search query suggestions that users would type into Facebook search. Maximum 10 keywords, but quality over quantity - if only 3-5 valid keywords exist, return only those. The search keyword is: "${keyword.trim()}". Return format: ["keyword1", "keyword2", "keyword3"]`;
          
          const visionResponse = await generateVisionContent(result.data.screenshot, visionPrompt, visionModel, apiKey);
          
          // Parse keywords from vision response
          let keywords = [];
          try {
            const jsonMatch = visionResponse.match(/\[.*?\]/s);
            if (jsonMatch) {
              keywords = JSON.parse(jsonMatch[0]);
            } else {
              const lines = visionResponse.split('\n').map(line => line.trim()).filter(line => line.length >= 3 && line.length <= 100);
              keywords = lines.filter(line => !line.includes('http') && !line.match(/\.(com|net|org|io)/i)).slice(0, 10);
            }
          } catch (parseError) {
            console.error('Error parsing vision response:', parseError);
            const lines = visionResponse.split('\n').map(line => line.trim()).filter(line => line.length >= 3 && line.length <= 100);
            keywords = lines.slice(0, 10);
          }
          
          if (keywords.length > 0) {
            // Use vision-extracted keywords
            rawData = {
              ...rawData,
              autocompleteSuggestions: keywords
            };
          }
          // If vision failed, use DOM extraction results (already in rawData)
        } catch (error) {
          console.error('Advanced mode vision API failed, using DOM extraction results:', error);
          // Use DOM extraction results (already in rawData)
        }
      }

      // Handle "Not Logged In" or "Empty" cases
      if (!rawData) {
        throw new Error('No data received from Facebook. Please ensure you are logged into Facebook and try again.');
      }
      
      // Check if we have meaningful data
      const autocompleteSuggestions = rawData.autocompleteSuggestions || [];
      const posts = rawData.posts || [];
      const hasAutocomplete = Array.isArray(autocompleteSuggestions) && autocompleteSuggestions.length > 0;
      const hasPosts = Array.isArray(posts) && posts.length > 0;
      
      // Log data for debugging
      console.log('Data received:', {
        autocompleteCount: autocompleteSuggestions.length,
        postsCount: posts.length,
        hasScreenshot: !!rawData.screenshot,
        autocompleteSample: autocompleteSuggestions.slice(0, 3),
        postsSample: posts.slice(0, 2)
      });
      
      if (!hasAutocomplete && !hasPosts) {
        throw new Error('No data found. The search returned empty results. This could mean:\n1. The keyword did not match any Facebook search suggestions\n2. You need to log in to Facebook\n3. Facebook\'s search interface has changed\n\nPlease try a different keyword or ensure you are logged into Facebook.');
      }
      
      // Warn if we have limited data
      if (!hasAutocomplete && hasPosts) {
        console.warn('Warning: No autocomplete suggestions found, but posts were found. Results may be limited.');
      }
      if (hasAutocomplete && !hasPosts) {
        console.warn('Warning: No posts found, but autocomplete suggestions were found. Results may be limited.');
      }

      // Step B: Send Data to AI
      setStatus('analyzing');
      const aiResponse = await generateAIReport(keyword.trim(), rawData);
      
      // Clean unwanted AI suggestions (ULTRA-CONSERVATIVE - only removes clear AI questions)
      const cleanedReport = cleanAIReport(aiResponse);
      
      // Convert markdown to HTML (headers, numbered lists, bold, italics)
      const htmlReport = markdownToHTML(cleanedReport);
      setReport(htmlReport);
      setStatus('success');
      recordSearch();
      checkRateLimit();

    } catch (error) {
      console.error('FB Search Trend error:', error);
      setStatus('error');
      
      // Provide user-friendly error messages
      let userMessage = error.message || 'An unexpected error occurred.';
      
      // Enhance specific error messages
      if (error.message.includes('log in') || error.message.includes('login')) {
        userMessage = 'Please log in to Facebook. Click "Login to Facebook" button to open the login window.';
      } else if (error.message.includes('No data found') || error.message.includes('empty results')) {
        userMessage = error.message; // Already user-friendly
      } else if (error.message.includes('API key')) {
        userMessage = 'OpenRouter API key is required. Please set it in Settings.';
      } else if (error.message.includes('rate limit') || error.message.includes('429')) {
        userMessage = 'Rate limit exceeded. Please wait a moment and try again.';
      }
      
      setErrorMessage(userMessage);
    }
  };

  // The AI Handler
  const generateAIReport = async (searchTerm, scrapedData) => {
    const apiKey = localStorage.getItem('openrouter_api_key');
    if (!apiKey || apiKey.trim() === '') {
      throw new Error('OpenRouter API key is required. Please set it in Settings.');
    }

    // Get default model or use a good analysis model
    // Prefer models that are known to work well with analysis tasks
    const defaultModel = localStorage.getItem('default_ai_model');
    const availableModels = await getAvailableModels(false, apiKey);
    
    // Try to find a reliable model for analysis
    // Prefer GPT models, then Gemini, then others
    let modelToUse = defaultModel;
    if (!modelToUse || modelToUse.includes('gemini-2.0-flash-exp')) {
      // Try GPT models first as they're more reliable
      const gptModel = availableModels.find(m => 
        m.id.includes('gpt-4') || m.id.includes('gpt-3.5')
      );
      if (gptModel) {
        modelToUse = gptModel.id;
      } else {
        // Fallback to Gemini or other available models
        const fallbackModel = availableModels.find(m => 
          m.id.includes('gemini') || m.id.includes('gpt') || m.id.includes('claude')
        );
        modelToUse = fallbackModel?.id || 'openai/gpt-3.5-turbo';
      }
    }

    const systemPrompt = `You are Fiddyscript's Senior Market Research Analyst. Your job is to analyze raw, unstructured social media data (scraped from Facebook) and transform it into a high-level Marketing Intelligence Report.

Core Directives:
- Filter Noise: Ignore posts that appear to be spam, bot-generated, or irrelevant ads. Focus only on authentic human interactions.
- Detect Intent: Look beyond the keywords to find the intent—what is the user actually trying to solve, buy, or learn?
- Cultural Nuance: Be sensitive to regional slang, idioms, or local context if present in the text.
- Actionable Output: Do not just summarize; provide specific recommendations for content creation.

Response Structure (Markdown): The output must be formatted in clean Markdown with the following sections:
- 📊 Executive Sentiment: (Positive/Negative/Neutral and a 1-sentence vibe check).
- 🔑 Top Autocomplete Keywords: (List the top 7-10 most relevant autocomplete suggestions from the scraped data. This is CRUCIAL - these keywords show what users are actually searching for. Format as a numbered or bulleted list. If fewer than 7 keywords are available, list all available ones).
- 😩 Top 3 Pain Points: (What are people struggling with?).
- ❓ Common Questions: (What specific questions are being asked? Use these for SEO).
- 🗣️ Voice & Tone: (Keywords, slang, or emotional triggers used by the audience).
- 💡 Content Opportunities: (Provide a comprehensive list of content ideas across multiple formats. This section MUST include ALL of the following subsections with specific, actionable ideas based on the scraped data):
  - 🎥 Video Ideas: (2-3 specific video content ideas with brief descriptions)
  - 📝 "How to" Ideas: (2-3 "How to" article or video ideas that address common questions or pain points)
  - 💡 Top Tips Ideas: (2-3 tips-based content ideas (e.g., "Top 5 Tips for...", "7 Ways to..."))
  - 🔥 Hot Topic Ideas: (2-3 trending or timely topic ideas that align with current search trends and user interests)
  
  Each idea should be specific, actionable, and directly tied to the insights from the scraped data. Format each subsection with clear titles and brief descriptions.`;

    const userPrompt = `Here is the raw data scraped for the keyword: "${searchTerm}".

Autocomplete Suggestions: ${JSON.stringify(scrapedData.autocompleteSuggestions || [])}

Posts Data: ${JSON.stringify(scrapedData.posts || [])}

IMPORTANT: The "Top Autocomplete Keywords" section is CRITICAL. Extract and list ONLY the valid, relevant keywords from the Autocomplete Suggestions array above. These keywords represent what users are actively searching for on Facebook and are essential for understanding search trends.

CRITICAL FILTERING RULES:
- DO NOT include URLs, domains (anything with .com, .net, .org, etc.), or website addresses
- DO NOT include random concatenated text or spam (e.g., "E5OkP.comOdofin", "OdofinGoodnight", "7-figureaffiliate.com")
- DO NOT pad the list to reach 10 keywords - if only 3-5 valid keywords exist, list only those
- ONLY include actual search query suggestions that users would type into Facebook search
- Maximum of 10 keywords, but quality over quantity - fewer valid keywords is better than including invalid ones
- Prioritize keywords that are most relevant to the search term "${searchTerm}"

Please generate a Marketing Intelligence Report in Markdown format with the sections specified above, making sure to:
1. Include the Top Autocomplete Keywords section prominently with ONLY valid, relevant keywords (no URLs, domains, or spam).
2. Provide comprehensive Content Opportunities with ALL four subsections (Video Ideas, "How to" Ideas, Top Tips Ideas, and Hot Topic Ideas) - each with 2-3 specific, actionable ideas based on the scraped data.`;

    try {
      // Some OpenRouter models don't support 'system' role, so combine system and user prompts
      // Format: Put system instructions at the start of the user message
      const combinedPrompt = `${systemPrompt}\n\n${userPrompt}`;
      
      const analysis = await generateContent(
        combinedPrompt,
        modelToUse,
        apiKey
      );

      return analysis;
    } catch (error) {
      console.error('AI generation error:', error);
      
      // Provide more specific error messages
      if (error.message.includes('API key')) {
        throw new Error('OpenRouter API key is required. Please set it in Settings.');
      } else if (error.message.includes('Rate limit') || error.message.includes('429')) {
        throw new Error('OpenRouter API rate limit exceeded. Please wait a moment and try again.');
      } else if (error.message.includes('401') || error.message.includes('403') || error.message.includes('Authorization')) {
        throw new Error('OpenRouter API key is invalid. Please check your API key in Settings.');
      } else if (error.message.includes('No endpoints found')) {
        throw new Error('Model not available. Please enable "Enable free endpoints that may publish prompts" at https://openrouter.ai/settings/privacy or try a different model.');
      } else if (error.message.includes('Provider returned error')) {
        throw new Error('AI model error. Please try a different AI model in Settings.');
      } else {
        throw new Error(`AI analysis failed: ${error.message}`);
      }
    }
  };

  const handleShowLogin = () => {
    if (window.electronAPI && window.electronAPI.fbSearchTrend) {
      window.electronAPI.fbSearchTrend.showLogin();
      setIsLoggedIn(null); // Reset to check again
      setTimeout(() => {
        window.electronAPI.fbSearchTrend.checkLogin().then((result) => {
          if (result.success) {
            setIsLoggedIn(result.isLoggedIn);
          }
        });
      }, 2000);
    }
  };

  // Helper to strip HTML tags for copying
  const stripHTML = (html) => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const handleCopyReport = async () => {
    if (report) {
      try {
        // Convert <br> back to newlines for plain text copy
        const plainText = report.replace(/<br>/gi, '\n').replace(/<[^>]*>/g, '');
        
        // Use the Electron API if available for better reliability
        if (window.electronAPI && window.electronAPI.copyToClipboard) {
          window.electronAPI.copyToClipboard(plainText);
          alert('Report copied to clipboard!');
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          // Fallback to Web Clipboard API
          await navigator.clipboard.writeText(plainText);
          alert('Report copied to clipboard!');
        } else {
          // Fallback: Create temporary textarea and copy
          const textarea = document.createElement('textarea');
          textarea.value = plainText;
          textarea.style.position = 'fixed';
          textarea.style.left = '-999999px';
          document.body.appendChild(textarea);
          textarea.select();
          textarea.setSelectionRange(0, 99999); // For mobile devices
          
          try {
            document.execCommand('copy');
            alert('Report copied to clipboard!');
          } catch (err) {
            console.error('Copy failed:', err);
            alert('Could not copy to clipboard. Please select and copy the text manually.');
          } finally {
            document.body.removeChild(textarea);
          }
        }
      } catch (error) {
        console.error('Copy error:', error);
        alert('Could not copy to clipboard. Please select and copy the text manually.');
      }
    }
  };

  return (
    <div className="fb-search-trend">
      <div className="fb-search-trend-header">
        <h2>📊 FB Search Trend</h2>
        <p>Discover what users are searching for on Facebook based on keywords</p>
        <div className="feature-banner">
          🔍 Analyzes Facebook search trends and provides marketing insights
        </div>
        
        {/* Login Status - Enhanced like Mobile */}
        {isLoggedIn === false && (
          <div className="login-prompt">
            <p>⚠️ Not logged into Facebook</p>
            <p className="login-prompt-subtext">You'll need to log in before searching</p>
            <button onClick={handleShowLogin} className="login-button">
              Login to Facebook
            </button>
          </div>
        )}
        {isLoggedIn === true && (
          <div className="login-status">
            ✅ Logged into Facebook
          </div>
        )}

        {/* Rate Limit Display - Enhanced like Mobile */}
        <div className="rate-limit-info">
          <div className="rate-limit-text">
            Remaining searches: <strong>{rateLimitInfo.remaining}/{RATE_LIMIT}</strong>
          </div>
          {rateLimitInfo.resetTime && (
            <div className="reset-time">
              Resets {rateLimitInfo.resetTime.toLocaleTimeString()}
            </div>
          )}
        </div>

        {/* Browser Window Toggle */}
        <div className="browser-window-toggle">
          <label className="browser-window-toggle-label">
            <input
              type="checkbox"
              checked={showBrowserWindow}
              onChange={(e) => {
                setShowBrowserWindow(e.target.checked);
                if (e.target.checked) {
                  window.electronAPI?.fbSearchTrend?.showWindow();
                } else {
                  window.electronAPI?.fbSearchTrend?.hideWindow();
                }
              }}
              disabled={status === 'scraping' || status === 'analyzing'}
            />
            <span className="browser-window-toggle-text">
              👁️ Show Browser Window (see extraction in real-time)
            </span>
          </label>
          <p className="browser-window-toggle-description">
            Toggle to show/hide the Facebook browser window during extraction. You can watch the data being extracted in real-time.
          </p>
        </div>
      </div>

      {/* Search Mode Toggle */}
      <div className="mode-toggle-section">
        <div className="mode-toggle-container">
          <label className="mode-toggle-label">
            <span className="mode-label-text">Normal Search</span>
            <span className="mode-label-description">(Free, faster)</span>
          </label>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={useAdvancedMode}
              onChange={(e) => setUseAdvancedMode(e.target.checked)}
              disabled={status === 'scraping' || status === 'analyzing'}
            />
            <span className="toggle-slider"></span>
          </label>
          <label className="mode-toggle-label">
            <span className="mode-label-text">Advanced Search</span>
            <span className="mode-label-description">(Uses API credits, more reliable)</span>
            {useAdvancedMode && <span className="advanced-badge">⚡ Active</span>}
          </label>
        </div>
        {useAdvancedMode && (
          <div className="advanced-mode-info">
            <span className="info-icon">ℹ️</span>
            <span>Advanced mode uses AI vision to extract all visible keywords. This consumes API credits.</span>
          </div>
        )}
      </div>

      {/* Search Input Section */}
      <div className="search-section">
        <input
          type="text"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="Enter a keyword (e.g., 'Real Estate Lagos', 'Affiliate Marketing')"
          className="search-input"
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          disabled={status === 'scraping' || status === 'analyzing'}
        />
        <button
          onClick={handleSearch}
          disabled={status === 'scraping' || status === 'analyzing' || rateLimitInfo.remaining === 0}
          className={`search-button ${status === 'scraping' || status === 'analyzing' ? 'processing' : ''}`}
        >
          {status === 'scraping' || status === 'analyzing' ? (
            <>
              <span className="button-spinner"></span>
              <span>Processing...</span>
            </>
          ) : (
            'Analyze'
          )}
        </button>
      </div>

      {/* Status Indicators - Enhanced like Mobile */}
      {status === 'scraping' && (
        <div className="status-message scraping">
          <div className="status-content">
            <div className="status-spinner"></div>
            <div className="status-text-container">
              <span>🕷️ Scanning Facebook for "{keyword}"...</span>
              {showBrowserWindow && (
                <span className="status-subtext">👁️ Browser window is visible - watch the extraction in real-time</span>
              )}
            </div>
          </div>
        </div>
      )}
      {status === 'analyzing' && (
        <div className="status-message analyzing">
          <div className="status-content">
            <div className="status-spinner"></div>
            <span>🧠 AI is analyzing sentiment and extracting trends...</span>
          </div>
        </div>
      )}
      {status === 'error' && errorMessage && (
        <div className="error-message">
          ❌ {errorMessage}
        </div>
      )}

      {/* Results Display */}
      {status === 'success' && report && (
        <div className="results-card">
          <div className="results-header">
            <h3>Marketing Intelligence Report</h3>
            <button onClick={handleCopyReport} className="copy-button">
              Copy Report
            </button>
          </div>
          <div className="report-content" dangerouslySetInnerHTML={{ __html: report }} style={{ whiteSpace: 'pre-wrap' }} />
        </div>
      )}
    </div>
  );
};

export default FBSearchTrend;
